import { Temporal } from 'temporal-polyfill';
import { endOfDay } from '.';

export type InstantInitializer = Temporal.Instant | string;

type BoundaryTime = string | Temporal.PlainDate | Temporal.Instant | null;

interface BoundaryTimeInstants {
  startBoundary: Temporal.Instant | null;
  endBoundary: Temporal.Instant | null;
}

interface BoundaryInstantProps {
  value?: BoundaryTime;
  kind: 'start' | 'end';
}

/**
 * A range of `Instant`s that is inclusive at the start and exclusive at the
 * end.
 */
export class InstantInterval {
  static from(start: InstantInitializer, end: InstantInitializer): InstantInterval {
    return new this(Temporal.Instant.from(start), Temporal.Instant.from(end));
  }

  static untilNow(ago: Omit<Temporal.DurationLike, 'years' | 'month' | 'week' | 'days'>): InstantInterval {
    const now = Temporal.Now.instant();
    const past = now.subtract(ago);
    return new this(past, now);
  }

  static ofEntireDay(data: Temporal.PlainDate, timeZone: string): InstantInterval {
    const start = data.toZonedDateTime(timeZone).startOfDay().toInstant();
    const end = endOfDay(start, timeZone);
    return new this(start, end);
  }

  static boundariesFromOptionalDates(
    timeZone: string,
    startOnOrAfter?: BoundaryTime,
    endOnOrBefore?: BoundaryTime,
  ): BoundaryTimeInstants {
    const toInstant = ({ value, kind }: BoundaryInstantProps): Temporal.Instant | null => {
      if (!value) {
        return null;
      }

      if (value instanceof Temporal.Instant) {
        return value;
      }

      if (value instanceof Temporal.PlainDate) {
        const { start, end } = InstantInterval.ofEntireDay(value, timeZone);
        return kind === 'start' ? start : end;
      }

      // If it is a string, try to parse it as Temporal.Instant, if not, parse to Temporal.PlainDate
      if (typeof value === 'string') {
        try {
          return Temporal.Instant.from(value);
        } catch {
          // Ignore parsing errors and try other formats
        }

        const plainDate = Temporal.PlainDate.from(value);
        const { start, end } = InstantInterval.ofEntireDay(plainDate, timeZone);
        return kind === 'start' ? start : end;
      }

      // Unsupported temporal value
      return null;
    };

    return {
      startBoundary: toInstant({ value: startOnOrAfter, kind: 'start' }),
      endBoundary: toInstant({ value: endOnOrBefore, kind: 'end' }),
    };
  }

  constructor(
    public start: Temporal.Instant,
    public end: Temporal.Instant,
  ) {
    validateInstantIntervalLike(start, end);
  }

  toString(): string {
    return `${this.start.toString()}/${this.end.toString()}`;
  }

  contains(instant: Temporal.Instant): boolean {
    return Temporal.Instant.compare(this.start, instant) <= 0 && Temporal.Instant.compare(instant, this.end) < 0;
  }

  isOverlapping(other: InstantInterval): boolean {
    if (this.start.equals(other.start) || this.end.equals(other.end)) {
      return true;
    }
    if (Temporal.Instant.compare(this.start, other.start) < 0) {
      return Temporal.Instant.compare(this.end, other.start) > 0;
    } else {
      return Temporal.Instant.compare(other.end, this.start) > 0;
    }
  }
}

export function validateInstantIntervalLike(start: Temporal.Instant, end: Temporal.Instant): void {
  if (Temporal.Instant.compare(start, end) > 0) {
    throw new RangeError("An InstantInterval's start must occur before the end");
  }
  if (start.equals(end)) {
    throw new RangeError('An InstantInterval cannot start and end at the same instant');
  }
}

export class InstantIntervalSet {
  intervals: InstantInterval[];

  constructor(items: Iterable<InstantInterval>) {
    this.intervals = [...items];

    if (!this.intervals.length) {
      throw new RangeError('A InstantIntervalSet must have at least one InstantInterval in it');
    }
  }

  toString(): string {
    return this.intervals.map((interval) => interval.toString()).join(',');
  }

  contains(instant: Temporal.Instant): boolean {
    for (const interval of this.intervals) {
      if (interval.contains(instant)) {
        return true;
      }
    }
    return false;
  }

  isOverlapping(other: InstantIntervalSet): boolean {
    for (const a of this.intervals) {
      for (const b of other.intervals) {
        if (a.isOverlapping(b)) {
          return true;
        }
      }
    }

    return false;
  }
}
